import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

const HOTWORDS = ["prompt", "tool", "agent", "loop", "eval", "ship"];

function Ticker() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HOTWORDS.length), 280);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="inline-block min-w-[7ch] text-[#2FCC7C] font-mono">
      <AnimatePresence mode="popLayout">
        <motion.span
          key={HOTWORDS[i]}
          initial={{ y: 16, opacity: 0, filter: "blur(4px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          exit={{ y: -16, opacity: 0, filter: "blur(4px)" }}
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block"
        >
          {HOTWORDS[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// The main title breaks into words for staggered reveal
const TITLE_WORDS = ["You", "got", "some", "skills?"];

export default function Intro() {
  const [shown, setShown] = useState(0);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    const timers = TITLE_WORDS.map((_, i) =>
      setTimeout(() => setShown((v) => v + 1), 180 + i * 140)
    );
    const revealTimer = setTimeout(
      () => setReveal(true),
      180 + TITLE_WORDS.length * 140 + 350
    );
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(revealTimer);
    };
  }, []);

  return (
    <section
      className="relative min-h-[100svh] flex items-center overflow-hidden px-6 md:px-12 lg:px-24 py-16"
      data-testid="intro-section"
    >
      {/* diagonal scanlines */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        <div
          className="w-full h-full"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, #2FCC7C 0px, #2FCC7C 1px, transparent 1px, transparent 40px)",
          }}
        />
      </div>

      {/* floating dots */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(18)].map((_, i) => {
          const seed = (i * 73) % 100;
          const size = 1 + (seed % 3);
          const green = seed % 2 === 0;
          return (
            <motion.span
              key={i}
              className="absolute rounded-full"
              style={{
                width: size,
                height: size,
                background: green ? "#2FCC7C" : "#2D7BF0",
                left: `${(seed * 7) % 100}%`,
                top: `${(seed * 11) % 100}%`,
                boxShadow: `0 0 ${size * 3}px ${green ? "#2FCC7C" : "#2D7BF0"}`,
              }}
              animate={{ y: [0, -14, 0], opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 3 + (seed % 4),
                repeat: Infinity,
                delay: (seed % 10) * 0.15,
              }}
            />
          );
        })}
      </div>

      <div className="relative z-10 w-full max-w-6xl mx-auto">
        {/* Header row */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-10 md:mb-14"
        >
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full glass">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[#2FCC7C] animate-ping opacity-75" />
              <span className="relative w-2 h-2 rounded-full bg-[#2FCC7C]" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-300">
              Challenge // 001
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-zinc-500 font-mono text-[10px] uppercase tracking-widest">
            <span className="w-8 h-px bg-gradient-to-r from-transparent via-[#2FCC7C] to-transparent" />
            <Ticker />
          </div>
        </motion.div>

        {/* Title */}
        <h1 className="font-heading font-black tracking-tighter leading-[0.95] text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          {TITLE_WORDS.map((word, i) => {
            const isLast = i === TITLE_WORDS.length - 1;
            const visible = i < shown;
            return (
              <span key={i} className="inline-block overflow-hidden align-baseline">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={visible ? { y: "0%" } : { y: "110%" }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className={`inline-block ${
                    isLast
                      ? "text-gradient-neon glow-text-green"
                      : "text-white"
                  }`}
                >
                  {word}
                </motion.span>
                {i < TITLE_WORDS.length - 1 && <span>&nbsp;</span>}
              </span>
            );
          })}
        </h1>

        {/* Sub */}
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={reveal ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 md:mt-10 max-w-2xl text-lg md:text-2xl text-zinc-400 leading-snug"
        >
          One week.{" "}
          <span className="text-white">One skill.</span>{" "}
          <span className="text-white">The whole team watching.</span>
        </motion.p>

        {/* Reveal ribbon */}
        <AnimatePresence>
          {reveal && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-12 md:mt-16 flex flex-wrap items-baseline gap-x-3 gap-y-2"
            >
              <span className="font-mono text-[10px] md:text-xs text-zinc-500 uppercase tracking-[0.3em]">
                → Enter
              </span>
              <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white">
                CareStack{" "}
                <span className="text-gradient-neon">AI-Skill Challenge</span>
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={reveal ? { opacity: 1 } : {}}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-10 md:mt-14 flex items-center gap-3 text-zinc-500"
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown className="w-4 h-4 text-[#2FCC7C]" strokeWidth={2.5} />
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-[#050505] pointer-events-none" />
    </section>
  );
}
